import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';
import { takeUntil } from 'rxjs/operators';

// Interfaces for type safety
interface ChatMessage {
  id?: number;
  message?: string;
  created_at?: string;
  sender?: boolean;
  receiver?: boolean;
  user_name?: string;
  name?: string;
  img?: string;
  has_photo?: boolean;
  can_delete?: boolean;
  user_id?: string;
  room_id?: string;
  room_type?: 'dm' | 'group';
}

interface SocketConfig {
  url: string;
  options: {
    transports: string[];
    autoConnect: boolean;
    reconnection: boolean;
    reconnectionDelay: number;
    reconnectionAttempts: number;
    timeout: number;
  };
}

interface RoomInfo {
  room_id: string;
  room_type: 'dm' | 'group';
  user_id?: string;
  group_id?: string;
}

interface TypingInfo {
  room_id: string;
  user_name: string;
  is_typing: boolean;
}

interface MessageReadInfo {
  room_id: string;
  message_id: number;
  user_id: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  // Modern dependency injection
  private readonly storage = inject(StorageService);

  // Socket instance
  private socket: Socket | null = null;
  private isConnected = false;
  private currentRoom: RoomInfo | null = null;

  // Cleanup
  private destroy$ = new Subject<void>();

  // Observables for real-time events
  private newMessageSubject = new BehaviorSubject<ChatMessage | null>(null);
  public newMessage$ = this.newMessageSubject.asObservable();

  private messageDeletedSubject = new Subject<number>();
  public messageDeleted$ = this.messageDeletedSubject.asObservable();

  private typingSubject = new BehaviorSubject<TypingInfo | null>(null);
  public typing$ = this.typingSubject.asObservable();

  private messageReadSubject = new Subject<MessageReadInfo>();
  public messageRead$ = this.messageReadSubject.asObservable();

  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  private errorSubject = new Subject<string>();
  public error$ = this.errorSubject.asObservable();

  constructor() {
    console.log('🔌 WebSocketService initialized');
  }

  /**
   * Initialize and connect to the WebSocket server
   */
  async connect(): Promise<void> {
    if (this.socket?.connected) {
      console.log('🔌 Socket already connected');
      return;
    }

    try {
      // Get authentication token
      const jwtTokenData = await this.storage.getString('jwttoken');
      const jwtToken = jwtTokenData?.value;

      if (!jwtToken) {
        console.error('❌ No JWT token found, cannot connect to WebSocket');
        return;
      }

      // Get user details
      const userIdData = await this.storage.getString('user_id');
      const clubIdData = await this.storage.getString('club_id');
      const userId = userIdData?.value;
      const clubId = clubIdData?.value;

      const config: SocketConfig = {
        url: environment.webSocketURL,
        options: {
          transports: ['websocket', 'polling'],
          autoConnect: false,
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionAttempts: 5,
          timeout: 10000
        }
      };

      console.log('🔌 Connecting to WebSocket server:', config.url);

      // Create socket connection with authentication
      this.socket = io(config.url, {
        ...config.options,
        auth: {
          token: jwtToken,
          user_id: userId,
          club_id: clubId
        }
      });

      this.setupSocketListeners();
      this.socket.connect();

    } catch (error) {
      console.error('❌ Error connecting to WebSocket:', error);
      this.errorSubject.next('Failed to connect to chat server');
    }
  }

  /**
   * Setup all socket event listeners
   */
  private setupSocketListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket?.id);
      this.isConnected = true;
      this.connectionStatusSubject.next(true);

      // Rejoin current room if we were in one
      if (this.currentRoom) {
        this.joinRoom(this.currentRoom);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
      this.isConnected = false;
      this.connectionStatusSubject.next(false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
      this.errorSubject.next('Connection error: ' + error.message);
    });

    // Chat events
    this.socket.on('new_message', (message: ChatMessage) => {
      console.log('📨 New message received:', message);
      this.newMessageSubject.next(message);
    });

    this.socket.on('message_deleted', (data: { message_id: number }) => {
      console.log('🗑️ Message deleted:', data.message_id);
      this.messageDeletedSubject.next(data.message_id);
    });

    this.socket.on('user_typing', (data: TypingInfo) => {
      console.log('⌨️ Received user_typing event from server:', JSON.stringify(data));
      this.typingSubject.next(data);
    });

    this.socket.on('message_read', (data: MessageReadInfo) => {
      console.log('👁️ Message read:', data);
      this.messageReadSubject.next(data);
    });

    // Error handling
    this.socket.on('error', (error: { message: string }) => {
      console.error('❌ Socket error:', error);
      this.errorSubject.next(error.message);
    });
  }

  /**
   * Join a chat room (DM or Group Chat)
   */
  joinRoom(roomInfo: RoomInfo): void {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot join room: socket not connected');
      return;
    }

    console.log('🚪 Joining room:', roomInfo);
    this.currentRoom = roomInfo;
    this.socket.emit('join_room', roomInfo);

    // Mark messages as read when joining room
    this.socket.emit('mark_room_read', { room_id: roomInfo.room_id });
  }

  /**
   * Leave the current chat room
   */
  leaveRoom(roomId: string): void {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot leave room: socket not connected');
      return;
    }

    console.log('🚪 Leaving room:', roomId);
    this.socket.emit('leave_room', { room_id: roomId });
    
    if (this.currentRoom?.room_id === roomId) {
      this.currentRoom = null;
    }
  }

  /**
   * Send a new message
   */
  sendMessage(roomInfo: RoomInfo, message: string, hasPhoto?: boolean, photoUrl?: string): void {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot send message: socket not connected');
      this.errorSubject.next('Not connected to chat server');
      return;
    }

    const messageData: ChatMessage = {
      room_id: roomInfo.room_id,
      room_type: roomInfo.room_type,
      message,
      has_photo: hasPhoto || false,
      img: photoUrl,
      created_at: new Date().toISOString()
    };

    console.log('📤 Sending message:', messageData);
    this.socket.emit('send_message', messageData);
  }

  /**
   * Broadcast a message that was already saved to the database
   * This only sends the message to other connected users in the room
   */
  broadcastMessage(roomId: string, messageData: any): void {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot broadcast message: socket not connected');
      return;
    }

    console.log('📤 Broadcasting saved message to room:', roomId);
    this.socket.emit('send_message', {
      room_id: roomId,
      message_data: messageData
    });
  }

  /**
   * Delete a message
   */
  deleteMessage(roomId: string, messageId: number): void {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot delete message: socket not connected');
      return;
    }

    console.log('🗑️ Deleting message:', messageId);
    this.socket.emit('delete_message', { room_id: roomId, message_id: messageId });
  }

  /**
   * Emit typing indicator
   */
  sendTypingIndicator(roomId: string, isTyping: boolean): void {
    if (!this.socket?.connected) return;

    console.log('📤 Emitting typing event:', { room_id: roomId, is_typing: isTyping });
    this.socket.emit('typing', { room_id: roomId, is_typing: isTyping });
  }

  /**
   * Mark a message as read
   */
  markMessageAsRead(roomId: string, messageId: number): void {
    if (!this.socket?.connected) return;

    this.socket.emit('mark_message_read', { room_id: roomId, message_id: messageId });
  }

  /**
   * Check if currently connected
   */
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  /**
   * Get current room info
   */
  getCurrentRoom(): RoomInfo | null {
    return this.currentRoom;
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting socket');
      
      if (this.currentRoom) {
        this.leaveRoom(this.currentRoom.room_id);
      }
      
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentRoom = null;
      this.connectionStatusSubject.next(false);
    }
  }

  /**
   * Reconnect to WebSocket server
   */
  reconnect(): void {
    console.log('🔄 Reconnecting socket');
    this.disconnect();
    this.connect();
  }

  ngOnDestroy(): void {
    console.log('🧹 Destroying WebSocketService');
    this.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
